"use client";

import { useEffect, useRef } from "react";

/**
 * 가로 스크롤 컨테이너에 마우스 drag + 휠 가로 변환 동작 추가 (2026-05-15).
 *
 * 모바일 touch 는 native overflow-x-auto 가 처리. 데스크탑 마우스 drag·휠 보강용.
 *
 * 사용:
 *   const ref = useDragScroll<HTMLDivElement>();
 *   <div ref={ref} className="overflow-x-auto hide-scrollbar flex gap-2">
 *     <button>chip 1</button> ...
 *   </div>
 *
 * 충돌 방지:
 *   - 5px 이상 drag 했으면 다음 click event 차단 (button onClick 의도치 않은 발화 방지)
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let movedDistance = 0;

    const onMouseDown = (e: MouseEvent) => {
      // 텍스트 입력 등 native interactive 요소는 제외
      const target = e.target as HTMLElement;
      if (target.closest("input, textarea, select")) return;
      isDown = true;
      movedDistance = 0;
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
      el.style.cursor = "grabbing";
    };

    const onMouseLeave = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "grab";
    };

    const onMouseUp = () => {
      if (!isDown) return;
      isDown = false;
      el.style.cursor = "grab";
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = x - startX;
      movedDistance = Math.abs(walk);
      el.scrollLeft = scrollLeft - walk;
    };

    // drag 후 click 차단 (button onClick 발화 방지)
    const onClickCapture = (e: MouseEvent) => {
      if (movedDistance > 5) {
        e.stopPropagation();
        e.preventDefault();
        movedDistance = 0;
      }
    };

    // 마우스 휠 세로 → 가로 (Shift 키 없어도)
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };

    el.addEventListener("mousedown", onMouseDown);
    el.addEventListener("mouseleave", onMouseLeave);
    el.addEventListener("mouseup", onMouseUp);
    el.addEventListener("mousemove", onMouseMove);
    el.addEventListener("click", onClickCapture, true);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.style.cursor = "grab";

    return () => {
      el.removeEventListener("mousedown", onMouseDown);
      el.removeEventListener("mouseleave", onMouseLeave);
      el.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("mousemove", onMouseMove);
      el.removeEventListener("click", onClickCapture, true);
      el.removeEventListener("wheel", onWheel);
    };
  }, []);

  return ref;
}
